import React from 'react';
import { GiftIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

/**
 * Componente de fallback para PackManager cuando hay problemas de carga
 */
const PackManagerFallback: React.FC = () => {
  const handleRetry = () => {
    window.location.reload();
  };

  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center space-x-3">
            <GiftIcon className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Gestión de Packs</h1>
              <p className="text-gray-600">Sistema de gestión de packs de pulseras con servicio incluido</p>
            </div>
          </div>
        </div>

        {/* Error Message */}
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="text-center">
            <ExclamationTriangleIcon className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Problema de Carga
            </h2>
            <p className="text-gray-600 mb-6">
              Hubo un problema al cargar el módulo de gestión de packs. 
              Esto puede deberse a una conexión lenta o un problema temporal.
            </p>
            
            <div className="space-y-4">
              <div className="flex justify-center space-x-4">
                <button
                  onClick={handleRetry}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Reintentar Carga
                </button>
                <button
                  onClick={handleGoBack}
                  className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Volver Atrás
                </button>
              </div>
              
              <div className="text-sm text-gray-500">
                <p>Si el problema persiste, contacta al administrador del sistema.</p>
              </div>
            </div>
          </div>

          {/* Información básica mientras se resuelve */}
          <div className="mt-8 border-t pt-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Funcionalidades del Módulo de Packs
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h4 className="font-medium text-gray-800">Gestión de Packs</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Crear y editar packs de pulseras</li>
                  <li>• Activar/desactivar packs</li>
                  <li>• Configurar precios por duración (12/24/36 meses)</li>
                  <li>• Gestión de características incluidas</li>
                  <li>• Pack Familiar con opciones x2, x4, x6</li>
                </ul>
              </div>
              <div className="space-y-3">
                <h4 className="font-medium text-gray-800">Pulseras y Servicio</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Configurar pulsera física incluida</li>
                  <li>• Duración del servicio por pack</li>
                  <li>• Descuentos familiares progresivos</li>
                  <li>• Diagnóstico del sistema de packs</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Calculadora básica de packs */}
          <div className="mt-8 border-t pt-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Packs Disponibles
            </h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-4">
                La gestión avanzada de packs estará disponible una vez que se resuelva el problema de carga.
              </p>
              <div className="grid md:grid-cols-4 gap-4 text-sm">
                <div className="bg-white p-3 rounded">
                  <div className="font-medium text-gray-800">Pack Gratis</div>
                  <div className="text-blue-600 font-semibold">$0</div>
                  <div className="text-gray-500">Servicio digital básico</div>
                  <div className="text-xs text-gray-400 mt-1">Sin pulsera física</div>
                </div>
                <div className="bg-white p-3 rounded border-2 border-blue-200">
                  <div className="font-medium text-gray-800">Pack Individual</div>
                  <div className="text-blue-600 font-semibold">$59.880</div>
                  <div className="text-gray-500">12 meses de servicio</div>
                  <div className="text-xs text-blue-600 mt-1">⭐ Más popular</div>
                </div>
                <div className="bg-white p-3 rounded">
                  <div className="font-medium text-gray-800">Pack Familiar</div>
                  <div className="text-blue-600 font-semibold">desde $113.772</div>
                  <div className="text-gray-500">x2, x4, x6 pulseras</div>
                  <div className="text-xs text-green-600 mt-1">Descuentos progresivos</div>
                </div>
                <div className="bg-white p-3 rounded">
                  <div className="font-medium text-gray-800">Pack Empresarial</div>
                  <div className="text-blue-600 font-semibold">Personalizado</div>
                  <div className="text-gray-500">Pulseras ilimitadas</div>
                  <div className="text-xs text-purple-600 mt-1">Cotización B2B</div>
                </div>
              </div>
            </div>
          </div>

          {/* Información técnica */}
          <div className="mt-8 border-t pt-8">
            <details className="group">
              <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                Información Técnica del Error
              </summary>
              <div className="mt-2 text-xs text-gray-500 bg-gray-50 p-3 rounded">
                <p><strong>Error:</strong> ChunkLoadError - Loading chunk failed</p>
                <p><strong>Componente:</strong> PackManager</p>
                <p><strong>Solución:</strong> Recargar la página o limpiar caché del navegador</p>
                <p><strong>Timestamp:</strong> {new Date().toLocaleString()}</p>
              </div>
            </details>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackManagerFallback;
